import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import { getSymbolsByCategory } from "../../utils/stitchSymbols";
import "./StitchGuideModal.css";

const StitchGuideModal = ({ open, onClose }) => {
  const stitchCategories = getSymbolsByCategory();

  const StitchChip = ({ symbol }) => (
    <Chip label={symbol} className="stitch-chip" />
  );

  const StitchItem = ({ stitch }) => (
    <ListItem className="stitch-item">
      <Box className="stitch-item-content">
        <Box className="stitch-symbol-container">
          <StitchChip symbol={stitch.symbol} />
        </Box>
        <Box className="stitch-details">
          <Typography variant="h6" className="stitch-meaning">
            {stitch.meaning}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            className="stitch-description"
          >
            {stitch.description}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={window.innerWidth < 600}
    >
      <DialogTitle className="stitch-guide-title">
        <Box>
          <Typography variant="h4" component="h2">
            Knitting Stitch Guide
          </Typography>
          <Typography variant="caption" className="stitch-guide-subtitle">
            Based on Craft Yarn Council Standards
          </Typography>
        </Box>
        <IconButton onClick={onClose}>×</IconButton>
      </DialogTitle>

      <DialogContent className="stitch-guide-content">
        {Object.entries(stitchCategories).map(
          ([categoryName, stitches], categoryIndex) => (
            <Box key={categoryName} className="stitch-category">
              <Typography variant="h6" className="stitch-category-title">
                {categoryName} Stitches
              </Typography>
              <List dense>
                {stitches.map((stitch, index) => (
                  <StitchItem key={index} stitch={stitch} />
                ))}
              </List>
              {categoryIndex < Object.keys(stitchCategories).length - 1 && (
                <Divider className="stitch-divider" />
              )}
            </Box>
          ),
        )}

        <Box className="stitch-pro-tips">
          <Typography variant="subtitle1" className="stitch-pro-tips-title">
            💡 Pro Tips
          </Typography>
          <Typography variant="body2" className="stitch-pro-tip">
            • Click any symbol in the key above to select it as your brush
          </Typography>
          <Typography variant="body2" className="stitch-pro-tip">
            • Use Tab or arrow keys to navigate between cells
          </Typography>
          <Typography variant="body2" className="stitch-pro-tip">
            • Large grids (20×20+) work best with zoom controls
          </Typography>
          <Typography variant="body2" className="stitch-pro-tip">
            • All symbols follow international Craft Yarn Council standards
          </Typography>

          <Divider className="stitch-pro-tips-divider" />

          <Typography
            variant="caption"
            color="text.secondary"
            className="stitch-links-intro"
          >
            Learn more about knitting:
          </Typography>
          <Box className="stitch-links">
            <Typography
              component="a"
              href="https://www.craftyarncouncil.com/standards/knit-chart-symbols"
              target="_blank"
              rel="noopener noreferrer"
              className="stitch-link"
            >
              Chart Symbols
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              className="stitch-link-separator"
            >
              •
            </Typography>
            <Typography
              component="a"
              href="https://www.craftyarncouncil.com/standards/knitting-abbreviations"
              target="_blank"
              rel="noopener noreferrer"
              className="stitch-link"
            >
              Abbreviations
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              className="stitch-link-separator"
            >
              •
            </Typography>
            <Typography
              component="a"
              href="https://www.craftyarncouncil.com/standards/how-to-read-knitting-pattern"
              target="_blank"
              rel="noopener noreferrer"
              className="stitch-link"
            >
              Reading Patterns
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              className="stitch-link-separator"
            >
              •
            </Typography>
            <Typography
              component="a"
              href="https://www.craftyarncouncil.com/knit_vs_crochet.php3"
              target="_blank"
              rel="noopener noreferrer"
              className="stitch-link"
            >
              Knit vs Crochet
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className="stitch-guide-actions">
        <Button onClick={onClose} variant="contained">
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StitchGuideModal;
